import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n/LanguageContext";
import { submitFamilyMealsInquiry } from "../lib/api";
import { trackFormAction } from "../lib/tracker";
import useRevealWhen from "../hooks/useRevealWhen";

const ROLE_FROM_QS = {
  "family-meals-supplier": "supplier",
  "family-meals-distributor": "distributor",
  supplier: "supplier",
  distributor: "distributor",
};

const initialForm = {
  role: "supplier",
  contact_name: "",
  email: "",
  business_name: "",
  business_type: "",
  location: "",
  surplus_volume: "",
  surplus_notes: "",
  organization_name: "",
  organization_type: "",
  service_area: "",
  capacity: "",
  community_needs: "",
};

const FamilyMealsJoin = () => {
  const { t } = useLanguage();
  const p = t.familyMeals.joinForm;
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successRef = useRevealWhen(submitted);

  useEffect(() => {
    const role = searchParams.get("role");
    if (role && ROLE_FROM_QS[role]) {
      setForm((f) => ({ ...f, role: ROLE_FROM_QS[role] }));
    }
  }, [searchParams]);

  const onChange = (e) => {
    const { name, value } = e.target;
    if (form.contact_name === "" && form.email === "") {
      trackFormAction("family-meals-join:start");
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const isSupplier = form.role === "supplier";
      const payload = {
        role: form.role,
        contact_name: form.contact_name.trim(),
        email: form.email.trim(),
        business_name: isSupplier ? form.business_name.trim() : null,
        business_type: isSupplier ? form.business_type.trim() : null,
        location: isSupplier ? form.location.trim() : null,
        surplus_volume: isSupplier ? form.surplus_volume.trim() || null : null,
        surplus_notes: isSupplier ? form.surplus_notes.trim() : null,
        organization_name: !isSupplier ? form.organization_name.trim() : null,
        organization_type: !isSupplier ? form.organization_type.trim() : null,
        service_area: !isSupplier ? form.service_area.trim() : null,
        capacity: !isSupplier ? form.capacity.trim() || null : null,
        community_needs: !isSupplier ? form.community_needs.trim() : null,
      };
      await submitFamilyMealsInquiry(payload);
      trackFormAction("family-meals-join:submit");
      setSubmitted(true);
      toast.success(p.success);
      setForm({ ...initialForm, role: form.role });
    } catch (err) {
      console.error(err);
      toast.error(p.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  const isSupplier = form.role === "supplier";

  return (
    <div data-testid="page-family-meals-join">
      <section className="border-b border-[#242424]">
        <div className="mise-container pt-16 md:pt-20 pb-10">
          <Link
            to="/family-meals"
            className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors"
            data-testid="fm-join-back"
          >
            <ArrowLeft size={14} />
            {p.returnCta}
          </Link>
          <p className="mt-8 mise-eyebrow">{p.eyebrow}</p>
          <h1 className="mt-4 text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
            {p.heading}
          </h1>
          <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">{p.sub}</p>
        </div>
      </section>

      <section className="mise-section">
        <div className="mise-container">
          {submitted ? (
            <div
              className="mise-card p-10 md:p-14 text-center max-w-2xl mx-auto outline-none"
              data-testid="fm-join-success"
              ref={successRef}
              tabIndex={-1}
            >
              <CheckCircle2 className="mx-auto text-[#E8490F]" size={48} />
              <h3 className="mt-6 text-2xl md:text-3xl font-semibold text-white">{p.success}</h3>
              <Link
                to="/family-meals"
                className="mt-8 inline-flex mise-pill-secondary"
                data-testid="fm-join-return"
              >
                <ArrowLeft size={16} />
                {p.returnCta}
              </Link>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mise-card p-8 md:p-10 grid gap-6 max-w-3xl mx-auto"
              data-testid="fm-join-form"
            >
              {/* Role selector */}
              <div className="grid gap-2">
                <label className="text-sm text-white/60" htmlFor="role">
                  {p.roleLabel}
                  <span className="text-[#E8490F]"> *</span>
                </label>
                <select
                  id="role"
                  name="role"
                  value={form.role}
                  onChange={onChange}
                  className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white"
                  data-testid="fm-join-role"
                >
                  <option value="supplier">{p.roles.supplier}</option>
                  <option value="distributor">{p.roles.distributor}</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <Field
                  label={p.common.contactName}
                  name="contact_name"
                  value={form.contact_name}
                  onChange={onChange}
                  required
                  testId="fm-join-contact-name"
                />
                <Field
                  label={p.common.email}
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  required
                  testId="fm-join-email"
                />
              </div>

              {isSupplier ? (
                <div className="grid gap-6" data-testid="fm-join-supplier-fields">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field
                      label={p.supplier.businessName}
                      name="business_name"
                      value={form.business_name}
                      onChange={onChange}
                      required
                      testId="fm-join-business-name"
                    />
                    <Field
                      label={p.supplier.businessType}
                      name="business_type"
                      value={form.business_type}
                      onChange={onChange}
                      placeholder={p.supplier.businessTypePlaceholder}
                      required
                      testId="fm-join-business-type"
                    />
                  </div>
                  <Field
                    label={p.supplier.location}
                    name="location"
                    value={form.location}
                    onChange={onChange}
                    placeholder={p.supplier.locationPlaceholder}
                    required
                    testId="fm-join-location"
                  />
                  <Field
                    label={p.supplier.surplusVolume}
                    name="surplus_volume"
                    value={form.surplus_volume}
                    onChange={onChange}
                    placeholder={p.supplier.surplusVolumePlaceholder}
                    testId="fm-join-surplus-volume"
                  />
                  <TextArea
                    label={p.supplier.surplusNotes}
                    name="surplus_notes"
                    value={form.surplus_notes}
                    onChange={onChange}
                    placeholder={p.supplier.surplusNotesPlaceholder}
                    required
                    testId="fm-join-surplus-notes"
                  />
                </div>
              ) : (
                <div className="grid gap-6" data-testid="fm-join-distributor-fields">
                  <div className="grid md:grid-cols-2 gap-6">
                    <Field
                      label={p.distributor.organizationName}
                      name="organization_name"
                      value={form.organization_name}
                      onChange={onChange}
                      required
                      testId="fm-join-org-name"
                    />
                    <Field
                      label={p.distributor.organizationType}
                      name="organization_type"
                      value={form.organization_type}
                      onChange={onChange}
                      placeholder={p.distributor.organizationTypePlaceholder}
                      required
                      testId="fm-join-org-type"
                    />
                  </div>
                  <Field
                    label={p.distributor.serviceArea}
                    name="service_area"
                    value={form.service_area}
                    onChange={onChange}
                    placeholder={p.distributor.serviceAreaPlaceholder}
                    required
                    testId="fm-join-service-area"
                  />
                  <Field
                    label={p.distributor.capacity}
                    name="capacity"
                    value={form.capacity}
                    onChange={onChange}
                    placeholder={p.distributor.capacityPlaceholder}
                    testId="fm-join-capacity"
                  />
                  <TextArea
                    label={p.distributor.communityNeeds}
                    name="community_needs"
                    value={form.community_needs}
                    onChange={onChange}
                    placeholder={p.distributor.communityNeedsPlaceholder}
                    required
                    testId="fm-join-community-needs"
                  />
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="mise-pill-primary self-start mt-2 disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="fm-join-submit"
              >
                {submitting ? p.sending : p.submit}
                {!submitting && <Send size={16} />}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", placeholder, required, testId }) => (
  <div className="grid gap-2">
    <label className="text-sm text-white/60" htmlFor={name}>
      {label}
      {required && <span className="text-[#E8490F]"> *</span>}
    </label>
    <input
      id={name}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30"
      data-testid={testId}
    />
  </div>
);

const TextArea = ({ label, name, value, onChange, placeholder, required, testId }) => (
  <div className="grid gap-2">
    <label className="text-sm text-white/60" htmlFor={name}>
      {label}
      {required && <span className="text-[#E8490F]"> *</span>}
    </label>
    <textarea
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      rows={5}
      className="bg-[#1a1a1a] border border-[#444444] focus:border-[#E8490F] outline-none rounded-xl px-4 py-3 text-white placeholder:text-white/30 leading-relaxed"
      data-testid={testId}
    />
  </div>
);

export default FamilyMealsJoin;
